import { AppStateEnum } from '../../types/app-state.types.ts';
import { ScriptKey, ScriptPhase } from '../../types/asset/asset.enums.ts';
import { AssetsFile, BaseLoaderUtil, Scripts } from '../../types/asset/asset.types.ts';
import { Lesson, LessonStep, LessonTask } from '../../types/asset/lesson.types.ts';
import { SpeakableScriptPart } from '../../types/asset/script.types.ts';
import { PrepareScriptToSpeakProps } from '../../types/script-processing-types.ts';

import { getCharactersUtil } from './characters.util.ts';
import { getLessonsUtil } from './lessons.util.ts';
import { getLettersUtil } from './letters.util.ts';
import { BaseScriptsProcessor } from './script-processors/base-scripts-processor.ts';
import { ScriptsFileLetterProcessor } from './script-processors/scripts-file-letter-processor.ts';
import { ScriptsFilePartsProcessor } from './script-processors/scripts-file-parts-processor.ts';
import { ScriptsFileUnknownProcessor } from './script-processors/scripts-file-unknown-processor.ts';
import { ScriptsPartsProcessor } from './script-processors/scripts-parts-processor.ts';
import { RegularProcessor } from './script-processors/scripts-regular-processor.ts';
import { getWordsUtil } from './words.util.ts';

export class ScriptsUtil implements BaseLoaderUtil {
  private static _instance: ScriptsUtil;
  private scripts: Scripts | null = null;

  private constructor(
    private readonly charactersUtil = getCharactersUtil(),
    private readonly lessonsUtil = getLessonsUtil(),
    private readonly lettersUtil = getLettersUtil(),
    private readonly wordsUtil = getWordsUtil()
  ) {
    // Private constructor to enforce singleton pattern
  }

  public static get instance(): ScriptsUtil {
    if (!ScriptsUtil._instance) {
      ScriptsUtil._instance = new ScriptsUtil();
    }
    return ScriptsUtil._instance;
  }

  public load(assets: AssetsFile): void {
    this.scripts = assets.scripts;
    const scriptCount = Object.values(this.scripts)
      .flatMap((taskTypeScripts) => Object.values(taskTypeScripts))
      .flatMap((stepTypeScripts) => Object.values(stepTypeScripts))
      .flatMap((phaseScripts) => Object.values(phaseScripts)).length;
    console.log(`Loaded ${scriptCount} scripts`);
  }

  public prepareScriptToSpeak(props: PrepareScriptToSpeakProps): SpeakableScriptPart[] {
    const { lessonTaskStep, taskStepViewCount, appState, scriptReplacements } = props;
    const { lesson, task, step } = lessonTaskStep;

    const scriptKey = this.determineScriptKey(appState, step, scriptReplacements);
    const script = this.getScriptToSpeak(lesson, task, step, taskStepViewCount, scriptKey, scriptReplacements);
    if (!script) return [];

    return this.splitStepScript(script, task, step);
  }

  private populateDefaultScriptReplacements(
    lesson: Lesson,
    task: LessonTask,
    step: LessonStep,
    scriptReplacements: Map<string, string>
  ): Map<string, string> {
    const letterKey = step.target ? step.target : task.letters?.[step.index ?? 0];
    const letter = letterKey ? this.lettersUtil.getLetter(letterKey) : null;

    const wordKey = step.target
      ? step.target
      : (task.words?.[step.index ?? 0] ?? task.story?.[step.index ?? 0]?.[step.subindex ?? 0]);
    const word = wordKey ? this.wordsUtil.getWord(wordKey) : null;

    const rhymingSoundKey = task.rhymingSounds?.[step.index ?? 0];
    const rhymingSoundWord = rhymingSoundKey ? this.wordsUtil.getWord(rhymingSoundKey) : null;

    const animalKey = lesson.animalCharacterKey;
    const animal = animalKey ? this.charactersUtil.getCharacter(animalKey) : null;

    const parts = this.lessonsUtil.getStepParts(task, step);

    const additionalValues = new Map<string, string | undefined>([
      ['story', task.story?.flat().join(' ')],
      ['letter.appearance', letter?.appearance],
      ['word.pronunciations[0]', word?.pronunciations?.[0]],
      ['rhymingSounds.pronunciations[0]', rhymingSoundWord?.pronunciations?.[0] ?? `\\(rhymingSounds.file)`],
      ['parts[0]', parts?.[0]],
      ['animal.name', animal?.name],
      ['animal.animalType', animal?.animalType],
    ]);

    const baseReplacements = new Map([
      ...this.removeUndefinedValues(additionalValues).entries(),
      ...scriptReplacements.entries(),
    ]);

    const finalReplacements = new Map([
      ...baseReplacements.entries(),
      ['content', this.replaceScriptReplacements(step.content ?? '', baseReplacements)],
      ['question', this.replaceScriptReplacements(step.question ?? '', baseReplacements)],
    ]);

    return this.removeUndefinedValues(finalReplacements);
  }

  private removeUndefinedValues(map: Map<string, string | undefined>): Map<string, string> {
    return new Map([
      ...Array.from(map.entries())
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, value] as [string, string]),
    ]);
  }

  private getScriptToSpeak(
    lesson: Lesson,
    task: LessonTask,
    step: LessonStep,
    taskStepViewCount: number,
    key: ScriptKey,
    scriptReplacements: Map<string, string>
  ): string {
    const phase = this.mapTaskStepViewCountToPhase(taskStepViewCount);
    const script = this.scripts?.[task.taskType]?.[step.stepType]?.[phase]?.[key];

    const allScriptReplacements = this.populateDefaultScriptReplacements(lesson, task, step, scriptReplacements);
    return this.replaceScriptReplacements(script ?? '', allScriptReplacements);
  }

  private replaceScriptReplacements(script: string, replacements: Map<string, string>): string {
    return (
      Array.from(replacements.entries()).reduce((script, [key, value]) => {
        return script?.replace(new RegExp(`\\\\\\(${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\)`, 'g'), value);
      }, script) ?? ''
    );
  }

  private determineScriptKey(
    appState: AppStateEnum,
    step: LessonStep,
    scriptReplacements: Map<string, string>
  ): ScriptKey {
    if (appState === AppStateEnum.ReadyToStart && step.scriptKey) {
      return step.scriptKey;
    } else {
      const key = appState as unknown as ScriptKey;
      if (key === ScriptKey.Incorrect) {
        if (scriptReplacements.has('incorrectWord')) {
          return ScriptKey.IncorrectWord;
        } else if (scriptReplacements.has('incorrectLetter')) {
          return ScriptKey.IncorrectLetter;
        } else if (scriptReplacements.has('reversedLetter')) {
          return ScriptKey.ReversedLetter;
        }
      }
      return key;
    }
  }

  private splitScript(script: string, splitOn: string[], task: LessonTask, step: LessonStep): SpeakableScriptPart[] {
    const allParts = this.splitScriptOn(script, splitOn);

    return this.processScriptParts(allParts, task, step);
  }

  private processScriptParts(parts: string[], task: LessonTask, step: LessonStep): SpeakableScriptPart[] {
    const partProcessor: BaseScriptsProcessor[] = [
      new ScriptsPartsProcessor(),
      new ScriptsFileLetterProcessor(),
      new ScriptsFilePartsProcessor(),
      new ScriptsFileUnknownProcessor(),
      new RegularProcessor(),
    ];
    return parts
      .map((part) => {
        for (const processor of partProcessor) {
          const result = processor.process(part, task, step);
          if (result.processed) {
            return result.parts;
          }
        }
        return [];
      })
      .flat();
  }

  private splitScriptOn(script: string, splitOn: string[]): string[] {
    return splitOn.reduce(
      (result, substring) => {
        return result.flatMap((part) => {
          const parts = part.split(substring);
          return parts
            .flatMap((part, i) => [part.trim()].concat(i < parts.length - 1 ? [substring] : []))
            .filter((part) => /[a-zA-Z0-9]/.test(part));
        });
      },
      [script]
    );
  }

  private splitStepScript(script: string, task: LessonTask, step: LessonStep): SpeakableScriptPart[] {
    const parts = this.lessonsUtil.getStepParts(task, step);
    const substrings = [
      '\\(letter.file)',
      '\\(parts)',
      ...(parts ? parts.map((_, i) => `\\(parts[${i}].file)`) : ['\\(parts[0].file)']),
      ...(task.rhymingSounds?.map((_, i) => `\\(rhymingSounds[${i}].file)`) ?? []),
      '\\(rhymingSounds.file)',
    ];
    return this.splitScript(script, substrings, task, step);
  }

  // This can be moved to a mapper and constants
  private mapTaskStepViewCountToPhase(taskStepViewCount: number): ScriptPhase {
    if (taskStepViewCount < 2) {
      return ScriptPhase.FirstTime;
    } else if (taskStepViewCount < 5) {
      return ScriptPhase.FewTimes;
    } else {
      return ScriptPhase.ManyTimes;
    }
  }
}

// Export a singleton instance
export const getScriptsUtil = (): ScriptsUtil => ScriptsUtil.instance;
